import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useParams, useNavigate, useBlocker } from 'react-router-dom'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Save, Trash2, Copy, ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import { PageHeader } from '@/components/layout'
import { Button } from '@/components/base/buttons'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import { LoadingSpinner } from '@/components/common'
import { AlertDialog } from '@/components/ui/AlertDialog'
import { useAssistant, useUpdateAssistant, useDeleteAssistant } from '@/hooks/api/useAssistants'
import { usePermissions, useIdentity } from '@/hooks/api'
import { useCreateRevision, generateRevisionMessage, getChangedFields } from '@/hooks/api/useRevisions'

// Shared form validation schema
import { createAssistantSchema, type AssistantFormData } from './schemas'

// Shared tab configuration
import {
  type TabId,
  getAssistantTabs,
  getVisibleTabs,
  tabHasErrors,
  DEFAULT_FORM_VALUES,
} from './config'

export function AssistantEditPage() {
  const { id, tab } = useParams<{ id: string; tab: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { can, canManage } = usePermissions()

  // Create schema with translated error messages
  const assistantSchema = useMemo(() => createAssistantSchema(t), [t])

  // Tabs configuration with translations
  const tabs = useMemo(() => getAssistantTabs(t), [t])

  // Valid tab IDs for validation
  const validTabIds = useMemo(() => tabs.map((t) => t.id), [tabs])

  // Get active tab from URL, default to 'about'
  const activeTab = (tab as TabId) || 'about'

  // Redirect to 'about' if invalid tab in URL
  useEffect(() => {
    if (tab === 'connections') {
      // Legacy URL from before the tab was renamed to 'whatsapp'.
      navigate(`/assistants/${id}/whatsapp`, { replace: true })
    } else if (tab && !validTabIds.includes(tab as TabId)) {
      navigate(`/assistants/${id}/about`, { replace: true })
    }
  }, [tab, validTabIds, id, navigate])

  // State
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false)

  // API hooks
  const { data: assistant, isLoading, error } = useAssistant(id)
  const updateAssistant = useUpdateAssistant()
  const deleteAssistant = useDeleteAssistant()
  const createRevision = useCreateRevision()
  const { user: identity } = useIdentity()

  // Ref to store the previous state before saving (for revision creation)
  const previousDataRef = useRef<Record<string, unknown> | null>(null)

  // Ref to bypass the navigation blocker after a successful save-and-close.
  // Using a ref instead of state because the blocker callback captures isDirty
  // via useCallback closure, so state changes aren't visible until the next render.
  const isSavingRef = useRef(false)

  // Form setup
  const methods = useForm<AssistantFormData>({
    resolver: zodResolver(assistantSchema),
    defaultValues: DEFAULT_FORM_VALUES,
  })

  const {
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting, errors },
  } = methods

  // Block in-app navigation when there are unsaved changes (React Router navigation)
  // Only block if navigating AWAY from this assistant's edit page (not when switching tabs)
  const assistantEditPathPrefix = `/assistants/${id}/`
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }: { currentLocation: { pathname: string }; nextLocation: { pathname: string } }) => {
        if (isSavingRef.current) return false

        const isStayingInSameAssistant =
          currentLocation.pathname.startsWith(assistantEditPathPrefix) &&
          nextLocation.pathname.startsWith(assistantEditPathPrefix)

        return isDirty && !isStayingInSameAssistant
      },
      [isDirty, assistantEditPathPrefix]
    )
  )

  // Handle blocker state changes - show dialog when navigation is blocked
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowUnsavedChangesDialog(true)
    }
  }, [blocker.state])

  // Block browser navigation (refresh, close tab, browser back button)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault()
        // Modern browsers require returnValue to be set
        e.returnValue = ''
        return ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  // Load assistant data into form and store for revision creation
  useEffect(() => {
    if (assistant) {
      const formData = {
        id: assistant.id,
        name: assistant.name,
        customer_id: assistant.customer_id,
        voice_id: assistant.voice_id,
        default_language: assistant.default_language,
        additional_languages: assistant.additional_languages || [],
        timezone: assistant.timezone,
        greeting: assistant.greeting,
        prompt: assistant.prompt,
        post_call_prompt: assistant.post_call_prompt,
        phone_number_id: assistant.phone_number_id,
        fallback_number: assistant.fallback_number,
        notification_user: assistant.notification_user,
        notification_outcomes: assistant.notification_outcomes || [],
        ai_model: assistant.ai_model,
        ai_deployment_id: assistant.ai_deployment_id,
        from_id: assistant.from_id,
        whatsapp_image: assistant.whatsapp_image,
        is_active: assistant.is_active,
        config: assistant.config || {},
        lexicon: assistant.lexicon || {},
        prompt_scenarios: assistant.prompt_scenarios || {},
      }

      // Store the ORIGINAL data from server for revision creation
      // This captures the state BEFORE any edits are made
      previousDataRef.current = JSON.parse(JSON.stringify(formData))

      reset(formData)
    }
  }, [assistant, reset])

  // Handlers
  const onSubmit = async (data: AssistantFormData) => {
    if (!id) return

    // Store the previous state before making changes
    const previousState = previousDataRef.current

    try {
      await updateAssistant.mutateAsync({
        id: Number(id),
        ...data,
      })

      // Create revision with the PREVIOUS state (before this save)
      // This allows users to revert to how the data was before this change
      if (previousState && identity?.id) {
        try {
          const changedFields = getChangedFields(
            previousState,
            data as Record<string, unknown>
          )

          // Only create revision if something actually changed
          if (changedFields.length > 0) {
            const message = generateRevisionMessage(changedFields)

            await createRevision.mutateAsync({
              resource: 'assistants',
              record_id: Number(id),
              data: previousState,
              author_id: identity.id,
              message,
            })
          }
        } catch (revisionError) {
          // Don't fail the save if revision creation fails
          console.error('Failed to create revision:', revisionError)
        }
      }

      // Update the stored previous state to the NEW data
      // So the next save will have the correct previous state
      previousDataRef.current = JSON.parse(JSON.stringify(data))

      toast.success(t('resources.assistants.save_success'))
      reset(data) // Reset form dirty state
    } catch (error: unknown) {
      const responseData = (error as { response?: { data?: { detail?: string; message?: string } } })?.response?.data
      const errorMessage = responseData?.message || responseData?.detail || t('resources.assistants.save_error')
      toast.error(errorMessage, {
        duration: 10000, // Show validation errors longer
      })
    }
  }

  const handleDelete = async () => {
    if (!id) return

    try {
      await deleteAssistant.mutateAsync(Number(id))
      toast.success(t('resources.assistants.delete_success'))
      navigate('/assistants')
    } catch (error: any) {
      toast.error(error.response?.data?.detail || t('resources.assistants.delete_error'))
    }
  }

  // Navigate back - useBlocker will intercept if there are unsaved changes
  const handleBack = () => {
    navigate('/assistants')
  }

  // Track whether the user confirmed leaving, so AlertDialog's
  // automatic onClose() call doesn't reset the blocker after proceeding.
  const isLeavingRef = useRef(false)

  // Handle confirming leave with unsaved changes (called from AlertDialog)
  const handleConfirmLeave = () => {
    isLeavingRef.current = true
    setShowUnsavedChangesDialog(false)
    if (blocker.state === 'blocked') {
      blocker.proceed()
    }
  }

  // Handle canceling leave (stay on page)
  const handleCancelLeave = () => {
    if (isLeavingRef.current) {
      isLeavingRef.current = false
      return
    }
    setShowUnsavedChangesDialog(false)
    if (blocker.state === 'blocked') {
      blocker.reset()
    }
  }

  // Save and close handler (saves then navigates back to list)
  const handleSaveAndClose = async (data: AssistantFormData) => {
    if (!id) return

    const previousState = previousDataRef.current

    try {
      await updateAssistant.mutateAsync({
        id: Number(id),
        ...data,
      })

      // Create revision with the PREVIOUS state
      if (previousState && identity?.id) {
        try {
          const changedFields = getChangedFields(
            previousState,
            data as Record<string, unknown>
          )

          if (changedFields.length > 0) {
            const message = generateRevisionMessage(changedFields)

            await createRevision.mutateAsync({
              resource: 'assistants',
              record_id: Number(id),
              data: previousState,
              author_id: identity.id,
              message,
            })
          }
        } catch (revisionError) {
          console.error('Failed to create revision:', revisionError)
        }
      }

      toast.success(t('resources.assistants.save_success'))
      isSavingRef.current = true
      navigate('/assistants')
    } catch (error: unknown) {
      const responseData = (error as { response?: { data?: { detail?: string; message?: string } } })?.response?.data
      const errorMessage = responseData?.message || responseData?.detail || t('resources.assistants.save_error')
      toast.error(errorMessage, {
        duration: 10000,
      })
    }
  }

  // Permissions
  const canEdit = can('edit:assistants')
  const canDelete = can('delete:assistants')
  const isAdminOrPartner = canManage

  // Filter tabs based on permissions
  const visibleTabs = useMemo(
    () => getVisibleTabs(tabs, { isAdminOrPartner, isEditMode: true }),
    [tabs, isAdminOrPartner]
  )

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  // Error state
  if (error || !assistant) {
    return (
      <div className="flex h-full flex-col">
        <PageHeader title={t('resources.generic.error')} />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <p className="text-lg text-gray-600 dark:text-gray-400">
              {t('resources.assistants.load_error')}
            </p>
            <Button onPress={handleBack} className="mt-4" iconLeading={ArrowLeft}>
              {t('resources.assistants.back_to_list')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex h-full min-w-0 flex-col overflow-x-hidden">
        {/* Header */}
        <PageHeader
          title={assistant.name}
          subtitle={`ID: ${assistant.id}${assistant.updated_at ? ` • ${t('resources.assistants.last_updated')}: ${new Date(assistant.updated_at).toLocaleDateString()}` : ''}`}
          backButton={{
            label: t('resources.assistants.back_to_list'),
            onClick: handleBack,
          }}
        />

        {/* Tabs */}
        <div className="min-w-0 flex-1 overflow-hidden">
          <Tabs
            selectedKey={activeTab}
            onSelectionChange={(key) => navigate(`/assistants/${id}/${key}`, { replace: true })}
            className="flex h-full min-w-0 flex-col"
          >
            {/* Tab List */}
            <div className="border-b border-gray-200 bg-white px-4 sm:px-6 dark:border-gray-700 dark:bg-gray-900">
              <TabsList>
                {visibleTabs.map((tab) => {
                  const hasErrors = tabHasErrors(tab.id, errors)
                  return (
                    <TabsTrigger key={tab.id} id={tab.id}>
                      <span className="flex items-center gap-2">
                        {tab.label}
                        {tab.badge && (
                          <span className="rounded-full bg-brand-50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-brand-700 dark:bg-brand-950 dark:text-brand-300">
                            {tab.badge}
                          </span>
                        )}
                        {hasErrors && (
                          <span className="flex h-2 w-2 rounded-full bg-red-500" title={t('app.validation.required')} />
                        )}
                      </span>
                    </TabsTrigger>
                  )
                })}
              </TabsList>
            </div>

            {/* Tab Panels */}
            <div className="min-w-0 flex-1 overflow-x-hidden">
              {visibleTabs.map((tab) => {
                const TabComponent = tab.component
                return (
                  <TabsContent key={tab.id} id={tab.id}>
                    <div className="w-full max-w-5xl px-4 py-4 sm:px-6 sm:py-6">
                      <TabComponent />
                    </div>
                  </TabsContent>
                )
              })}
            </div>
          </Tabs>
        </div>

        {/* Bottom Toolbar - hidden on Connections tab (form saves itself) */}
        <div className={`border-t border-gray-200 bg-white px-4 py-3 sm:px-6 sm:py-4 dark:border-gray-700 dark:bg-gray-900 ${activeTab === 'whatsapp' ? 'hidden' : ''}`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
            {/* Left side: Save & close, Save */}
            <div className="flex items-center gap-2">
              {canEdit && (
                <>
                  <Button
                    type="button"
                    isDisabled={!isDirty || isSubmitting}
                    isLoading={isSubmitting}
                    onPress={handleSubmit(handleSaveAndClose)}
                    className="flex-1 sm:flex-none"
                    iconLeading={Save}
                  >
                    <span className="hidden sm:inline">{t('resources.generic.save_and_close', { defaultValue: 'Save & close' })}</span>
                    <span className="sm:hidden">{t('resources.generic.save', { defaultValue: 'Save' })}</span>
                  </Button>
                  <Button
                    type="submit"
                    color="tertiary"
                    isDisabled={!isDirty || isSubmitting}
                    className="hidden sm:inline-flex"
                    iconLeading={Save}
                  >
                    {t('resources.generic.save', { defaultValue: 'Save' })}
                  </Button>
                </>
              )}
            </div>

            {/* Right side: Payment Link, Clone, Delete */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Payment Link - admin/partner only */}
              {canManage && id && (
                <Button
                  type="button"
                  color="tertiary"
                  size="sm"
                  onClick={() => {
                    const url = `${window.location.origin}/signup/payment?assistant_id=${id}`
                    navigator.clipboard.writeText(url)
                    toast.success(t('resources.assistants.payment_link_copied', { defaultValue: 'Payment link copied to clipboard' }))
                  }}
                  className="flex-1 sm:flex-none"
                  iconLeading={Copy}
                >
                  <span className="hidden sm:inline">{t('resources.assistants.copy_payment_link', { defaultValue: 'Payment Link' })}</span>
                </Button>
              )}

              {/* Clone - admin/partner only */}
              {canManage && (
                <Button
                  type="button"
                  color="tertiary"
                  size="sm"
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: 'instant' })
                    navigate(`/assistants/create/clone/${id}/about`)
                  }}
                  className="flex-1 sm:flex-none"
                  iconLeading={Copy}
                >
                  <span className="hidden sm:inline">{t('resources.assistants.clone', { defaultValue: 'Clone' })}</span>
                </Button>
              )}

              {/* Delete */}
              {canDelete && (
                <Button
                  type="button"
                  color="tertiary"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex-1 text-red-600 hover:text-red-700 sm:flex-none dark:text-red-400 dark:hover:text-red-300"
                  iconLeading={Trash2}
                >
                  <span className="hidden sm:inline">{t('resources.generic.delete', { defaultValue: 'Delete' })}</span>
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Delete confirmation dialog */}
        <AlertDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          title={t('resources.assistants.delete_confirm_title')}
          description={t('resources.assistants.delete_confirm_message', { name: assistant.name })}
          confirmLabel={t('resources.generic.delete')}
          variant="error"
        />

        {/* Unsaved changes confirmation dialog */}
        <AlertDialog
          isOpen={showUnsavedChangesDialog}
          onClose={handleCancelLeave}
          onConfirm={handleConfirmLeave}
          title={t('resources.assistants.unsaved_changes')}
          description={t('resources.assistants.unsaved_changes_warning')}
          confirmLabel={t('resources.assistants.unsaved_changes_leave')}
          cancelLabel={t('resources.assistants.unsaved_changes_stay')}
          variant="warning"
        />
      </form>
    </FormProvider>
  )
}

export default AssistantEditPage
